import { useState, useEffect } from "react";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { authedFetch } from "@/lib/auth";
import { IamuWordmark } from "@/components/brand/logo";

type AuthState =
  | "loading"
  | "needs-login"
  | "ready"
  | "authorizing"
  | "done"
  | "error";

const WEB_APP_URL =
  (import.meta.env.VITE_WEB_APP_URL as string | undefined) ||
  "https://iamu.dev";

export function CliLoginPage() {
  const [state, setState] = useState<AuthState>("loading");
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("sessionId");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      setError("Missing session ID. Please run `iamu init` again.");
      return;
    }

    // User is already authenticated via httpOnly cookie.
    // Verify by calling /api/auth/me — if it fails, redirect to login.
    authedFetch("/api/auth/me")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.tenantId) {
          setEmail(data.email ?? data.displayName ?? null);
          setState("ready");
        } else {
          const returnTo = `/cli-login?sessionId=${sessionId}`;
          window.location.href = `/login?redirect=${encodeURIComponent(returnTo)}`;
        }
      })
      .catch(() => {
        const returnTo = `/cli-login?sessionId=${sessionId}`;
        window.location.href = `/login?redirect=${encodeURIComponent(returnTo)}`;
      });
  }, [sessionId]);

  async function handleAuthorize() {
    if (!sessionId) return;
    setState("authorizing");
    setError(null);

    try {
      const res = await authedFetch(
        `/auth/cli/sessions/${sessionId}/authorize`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Failed to authorize (${res.status})`);
      }

      setState("done");
    } catch (err) {
      setState("error");
      setError(err instanceof Error ? err.message : "Authorization failed");
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 text-foreground">
      <div className="w-full max-w-md">
        <div className="rounded-3xl sm:rounded-[32px] border border-border bg-card/90 px-5 py-6 sm:px-8 sm:py-9 shadow-[0_18px_60px_rgba(22,19,17,0.08)] backdrop-blur-xl">
          <div className="mb-6">
            <IamuWordmark />
          </div>
          {state === "loading" && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-base text-muted-foreground">
                Checking authentication...
              </p>
            </div>
          )}

          {state === "ready" && (
            <>
              <div className="mb-6">
                <div className="text-sm font-medium text-primary">
                  CLI authorization
                </div>
                <h1 className="mt-2 text-xl font-semibold tracking-tight text-foreground">
                  Authorize your terminal
                </h1>
                <p className="mt-2 text-base text-muted-foreground">
                  The Iamu CLI is requesting access to your account.
                </p>
              </div>

              {email && (
                <div className="mb-6 rounded-2xl border border-border bg-muted px-5 py-4">
                  <p className="text-sm text-muted-foreground">Signed in as</p>
                  <p className="mt-1 text-base font-medium text-foreground">
                    {email}
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={handleAuthorize}
                className="flex h-12 w-full items-center justify-center rounded-full bg-primary px-5 text-base font-semibold text-primary-foreground transition-all hover:bg-primary/90 active:scale-[0.99]"
              >
                Authorize CLI access
              </button>

              <p className="mt-4 px-1 text-center text-xs text-muted-foreground">
                By authorizing, you agree to Iamu&apos;s{" "}
                <a
                  href={`${WEB_APP_URL}/terms`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  Terms
                </a>{" "}
                and{" "}
                <a
                  href={`${WEB_APP_URL}/privacy`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </>
          )}

          {state === "authorizing" && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-base text-muted-foreground">Authorizing...</p>
            </div>
          )}

          {state === "done" && (
            <div className="flex flex-col items-center gap-4 py-8">
              <CheckCircle2 className="h-10 w-10 text-finding-helps" />
              <div className="text-center">
                <h2 className="text-lg font-semibold text-foreground">
                  CLI authorized
                </h2>
                <p className="mt-2 text-base text-muted-foreground">
                  You can close this tab and return to your terminal.
                </p>
              </div>
            </div>
          )}

          {state === "error" && (
            <div className="flex flex-col items-center gap-4 py-8">
              <XCircle className="h-10 w-10 text-destructive" />
              <div className="text-center">
                <h2 className="text-lg font-semibold text-foreground">
                  Authorization failed
                </h2>
                <p className="mt-2 text-base text-muted-foreground">{error}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
